import React, { useCallback, useEffect } from 'react'
import { FormFields } from '~/components/FormFields'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { Checkbox, Form, Grid, Icon, Input, Popup } from 'semantic-ui-react'
import { CheckClimaEl } from '~/pages/Farmer/Co2Climate/style'
import { formatNumber, splitEvery } from '~/utils/helpers'
import LinkPopup from '../LinkPopup'
import InfoPopup from '~/components/InfoPopup'
import WarningIcon from '../WarningIcon'
import ErrorIcon from '../ErrorIcon'

interface Props {
  housingTypes: any[]
}

const FifthStep: React.FC<Props> = ({ housingTypes }) => {
  const { control, getValues, formState: { isSubmitted, errors }, clearErrors, setValue } = useFormContext()
  const watchCowsNumber = useWatch({ control, name: 'cowsNumber' })
  const transformedCowsNumber = watchCowsNumber.toString().replace(',', '.')

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'cowsHousingTypes',
    keyName: 'key'
  })

  const fieldNames = [
    'pastureGroup',
    'pastureDays',
    'pastureDuration'
  ]

  const [
    watchPastureGroup,
    watchPastureDays,
    watchPastureDuration
  ] = useWatch({ control, name: fieldNames }) || [];

  useEffect(() => {
    const hasError = Boolean(Object.keys(errors).find(errorKey => fieldNames.find(name => name === errorKey)))
    const emptyFields = !(Boolean(watchPastureDays) || Boolean(watchPastureDuration) || Boolean(watchPastureGroup))
    if (hasError && emptyFields) {
      clearErrors(fieldNames)
    }
  }, [Object.keys(errors).length, watchPastureDays, watchPastureDuration, watchPastureGroup])

  function handleChecked(field, checked) {
    const tempFields = checked ? [...fields, { ...field }] : fields.filter(housingType => housingType['id'] !== field.id)
    const hasManureTypes = tempFields.find(cowHousingType => cowHousingType?.produceSlurryManure === 1)

    if(!hasManureTypes) {
      setValue('cowsManureStorageTypes', [])
    }
    
    if (checked) {
      append({
        ...field,
        cattleNumber: ''
      })
      return
    }
    const index = fields.findIndex(fieldManure => fieldManure['id'] === field.id)
    if (index >= 0) {
      remove(index)
    }
  }

  function getTotalEnteredCows() {
    return fields.reduce((previousValue, currentValue) => {
      return previousValue + parseFloat(currentValue['cattleNumber']?.toString()?.replace(',', '.') || 0)
    }, 0)
  }

  function checkCowsTotal() {
    const cowsEnteredTotal = getTotalEnteredCows()
    return cowsEnteredTotal != transformedCowsNumber
  }

  const renderLabel = useCallback(() => {
    const cowsEnteredTotal = getTotalEnteredCows()
    const hasErrors = isSubmitted && cowsEnteredTotal != transformedCowsNumber
    return (
      <label
        style={{
          fontSize: '15px',
          color: hasErrors ? 'red' : 'initial'
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div>{`Milchkühe [${cowsEnteredTotal} / ${formatNumber(transformedCowsNumber, 0)}]`}</div>
          <InfoPopup
            content={'Diese Werte dienen der Berechnung der benötigten Einstreumengen sowie der Mengen von Gülle, Jauche und Festmist, die auf dem Betrieb anfallen.'}
            styleIcon={{ fontSize: '1.2em' }}
            position={'top center'}
          />
        </div>
      </label>
    );
  }, [fields]);

  const optionsSplit = splitEvery(housingTypes, 4)

  const pastureFieldsRequired = Boolean(watchPastureDays) || Boolean(watchPastureDuration) || Boolean(watchPastureGroup)

  return <div>
    <Form.Field>
      {renderLabel()}
      <CheckClimaEl.StyledDivider />
      <Grid columns={2} style={{ marginTop: '1em' }}>
        <Controller
          name={'cowsHousingTypes'}
          control={control}
          rules={{
            validate: () => !checkCowsTotal()
          }}
          render={({ fieldState }) => {
            const error = fieldState.error ? checkCowsTotal() : false
            return <>
              {optionsSplit.map((optionSplit) => {
                return <Grid.Column key={optionSplit.key}>
                  <Grid columns={2}>
                    {optionSplit.map((option) => {
                      const fieldIndex = fields.findIndex(field => field['id'] === option.id)
                      return <Grid.Row style={{ alignItems: 'center', padding: '0.6rem 0rem' }}
                        key={option.id}
                      >
                        <Grid.Column>
                          <Checkbox
                            label={<label style={{ display: 'flex', gap: 5 }}>
                              <div>{option.label}</div>
                              {option.cowsTooltip && <InfoPopup
                                content={option.cowsTooltip}
                                styleIcon={{ fontSize: '1.2em' }}
                                position={'top center'}
                              />}
                            </label>}
                            checked={Boolean(fields.find(manureField => manureField['id'] === option.id))}
                            onChange={(e, data) => handleChecked(option, data.checked)}
                          />
                        </Grid.Column>
                        <Grid.Column>
                          {fieldIndex >= 0 ? <FormFields.Input
                            control={control}
                            style={{ width: 200 }}
                            styleLabel={{ marginBottom: 0 }}
                            error={error}
                            typeInput={'controled'}
                            suffix={' Milchkühe'}
                            onBlur={() => {
                              const value = getValues(`cowsHousingTypes[${fieldIndex}].cattleNumber`)
                              update(fieldIndex, {
                                ...fields[fieldIndex],
                                cattleNumber: value
                              })
                            }}
                            noErrorMessage
                            withoutAsterisk
                            rules={{
                              required: true
                            }}
                            name={`cowsHousingTypes[${fieldIndex}].cattleNumber`}
                            placeholder={'Milchkühe'}
                          /> : <Input
                            style={{ width: 200 }}
                            disabled
                            placeholder={'Milchkühe'}
                          />
                          }
                        </Grid.Column>
                      </Grid.Row>
                    })}
                  </Grid>
                </Grid.Column>
              })}
            </>
          }}
        />
      </Grid>
    </Form.Field>
    <Grid columns={3} style={{ marginTop: '2em' }}>
      <Grid.Row>
        <Grid.Column>
          <FormFields.Input
            control={control}
            name={'pastureGroup'}
            suffix={' Milchkühe'}
            noErrorMessage={!watchPastureGroup}
            withoutAsterisk
            label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <label>{'Weidegruppe'}</label>
              <InfoPopup
                content={'Wie viele Milchkühe hielten sie auf der Weide?'}
                styleIcon={{ fontSize: '1.2em' }}
                position={'top center'}
              />
              <LinkPopup
                content={'Weidetagebuch'}
              />
            </div>}
            typeInput='controled'
            styleErrorLabel={{ position: 'absolute', zIndex: 999, marginTop: '0.5em' }}
            rules={{
              required: pastureFieldsRequired,
              min: 0,
              max: {
                value: parseInt(watchCowsNumber),
                message: `Die Anzahl der Milchkühe in der Weidegruppe (${watchPastureGroup}) muss kleiner gleich Ihrem  ⌀-Bestand (${watchCowsNumber}) sein.`
              }
            }}
            placeholder={'Anzahl Milchkühe'}
          />
        </Grid.Column>
        <Grid.Column>
          <FormFields.Input
            control={control}
            name={'pastureDays'}
            suffix={' Tage/Jahr'}
            noErrorMessage={!watchPastureDays}
            withoutAsterisk
            label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <label>{'Weidetage'}</label>
              <LinkPopup
                content={'Weidetagebuch'}
              />
              {watchPastureDays && (watchPastureDays < 40 || watchPastureDays > 240) && <Popup
                content={'Die Anzahl an Weidetagen liegt außerhalb des Normalbereichs von mindestens 40 Tagen und maximal 240 Tagen'}
                trigger={<Icon name='warning sign' color='yellow' style={{ fontSize: '1.2em' }} />}
              />}
            </div>}
            typeInput='controled'
            styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em' }}
            rules={{
              required: pastureFieldsRequired,
              min: 0,
              max: {
                value: 365,
                message: 'Die maximale Anzahl der Weidetage pro Jahr beträgt 365 Tage.'
              }
            }}
            placeholder={'Tage/Jahr'}
          />
        </Grid.Column>
        <Grid.Column>
          <FormFields.Input
            control={control}
            name={'pastureDuration'}
            noErrorMessage={!watchPastureDuration}
            suffix={' Stunden/Tag'}
            withoutAsterisk
            label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <label>{'Weidehaltungsdauer'}</label>
              <LinkPopup
                content={'Weidetagebuch'}
              />
              {watchPastureDuration < 3 && watchPastureDuration > 0 && <WarningIcon
                control={control}
                name='pastureDuration'
                content={'Die Weidehaltungsdauer liegt unter dem Normalbereich von mindestens 3 Stunden pro Tag.'}
              />}
              <ErrorIcon
                control={control}
                name='pastureDuration'
              />
            </div>}
            typeInput='controled'
            rules={{
              required: pastureFieldsRequired,
              min: 0,
              max: {
                value: 24,
                message: 'Die maximale Weidehaltungsdauer pro Tag beträgt 24 Stunden'
              }
            }}
            placeholder={'Stunden/Tag'}
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </div>
}
export default FifthStep